import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@compiled/react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { SourcesSidePanelCard } from '@atlassian/lumina/sources-side-panel-card';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerDialogClick } from '../../../ai-answer-dialog/on-ai-answer-dialog-click';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SainAction } from '../../controller/analytics/hooks/sain-action';
import { useKnowledgeSmartCardAnalytics } from '../knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesStack } from '../sources-stack/sources-stack';

const messages = defineMessages({
	sources_button_text: {
		id: 'search_ai.sources_side_panel_button_text',
		defaultMessage: '{numSources, plural, one {Source} other {Sources}}',
		description: 'Sources button text that opens side panel',
	},
	sources_panel_title: {
		id: 'search_ai.sources_side_panel_title',
		defaultMessage:
			'{numSources, plural, one {{numSources, number} Source} other {{numSources, number} Sources}}',
		description: 'Title shown in the sources side panel',
	},
	sources_panel_teamwork_graph_title: {
		id: 'search_ai.sources_side_panel_teamwork_graph_title',
		defaultMessage: 'Teamwork Graph',
		description: 'Teamwork graph title shown in the sources side panel',
	},
	sources_number_teamwork_graph: {
		id: 'search_ai.sources_number_teamwork_graph',
		defaultMessage:
			'{numSources, plural, one {Source ({numSources, number})} other {Sources ({numSources, number})}}',
		description: 'Title shown in the sources side panel',
	},
	close_panel: {
		id: 'search_ai.sources_side_panel_close',
		defaultMessage: 'Close sources panel',
		description: 'Accessible label for close button on sources side panel',
	},
});

const styles = cssMap({
	panel: {
		position: 'fixed',
		top: token('space.600'),
		right: token('space.0'),
		bottom: token('space.0'),
		width: '419px',
		backgroundColor: token('elevation.surface.overlay'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		display: 'flex',
		flexDirection: 'column',
	},
	panelHeaderContainer: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
	panelHeader: {
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '77px',
		flexShrink: 0,
	},
	panelHeaderDivider: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	panelHeaderTeamworkGraph: {
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '64px',
		flexShrink: 0,
	},
	panelContent: {
		gap: token('space.150'),
		overflowY: 'auto',
		flexGrow: 1,
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	sourceItem: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

type SourcesSidePanelProps = {
	/** Renders the panel trigger. Receives the toggle handler and a ref for focus return on close. */
	renderTrigger?: (
		onToggle: () => void,
		triggerRef: React.RefObject<HTMLButtonElement>,
	) => React.ReactNode;
};

export const SourcesSidePanel = ({
	renderTrigger,
}: SourcesSidePanelProps = {}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const context = useAIAnswerContext();
	const { commonAttributes } = context;
	const { sources } = commonAttributes;
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const panelRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const { mounted: isChatMounted } = useUIStore();
	const prevChatMounted = useRef(isChatMounted);

	useEffect(() => {
		if (isChatMounted && !prevChatMounted.current) {
			setIsOpen(false);
		}
		prevChatMounted.current = isChatMounted;
	}, [isChatMounted]);

	useEffect(() => {
		if (isOpen && panelRef.current) {
			panelRef.current.focus();
		}
	}, [isOpen]);

	const publish = usePublish('ai-mate');

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => {
			if (!prev) {
				fireAnalyticsEvent(
					onAIAnswerDialogClick({
						...commonAttributes,
						elementInteracted: 'sources-side-panel-button',
					}),
				);
				fireSAINUsageEvent({ metCriteria: SainAction.VIEW_ALL_SOURCES, commonAttributes });
				if (isChatMounted) {
					publish({ type: 'chat-close', source: 'sain', data: {} });
				}
			}
			return !prev;
		});
	}, [isChatMounted, publish, fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		triggerRef.current?.focus();
	}, []);

	if (!sources || sources.length === 0) {
		return null;
	}

	const numSources = sources.length;

	return (
		<>
			{renderTrigger ? (
				renderTrigger(handleToggle, triggerRef)
			) : (
				<Button onClick={handleToggle} testId="sources-side-panel-button">
					<Inline space="space.050" alignBlock="center">
						<SourcesStack sources={sources} />
						{formatMessage(messages.sources_button_text, { numSources })}
					</Inline>
				</Button>
			)}
			{isOpen &&
				createPortal(
					<Box xcss={styles.panel} testId="sources-side-panel" ref={panelRef} tabIndex={-1}>
						{fg('kd_sain_sources_twg_updates') ? (
							<Stack xcss={styles.panelHeaderContainer}>
								<Inline xcss={styles.panelHeaderTeamworkGraph}>
									<Inline alignBlock="center" space="space.100">
										<TeamworkGraphIcon label="" />
										<Heading as="h2" size="xsmall">
											{formatMessage(messages.sources_panel_teamwork_graph_title)}
										</Heading>
									</Inline>
									<IconButton
										appearance="subtle"
										icon={CrossIcon}
										label={formatMessage(messages.close_panel)}
										onClick={handleClose}
									/>
								</Inline>
								<Text color="color.text.subtle" weight="bold">
									{formatMessage(messages.sources_number_teamwork_graph, { numSources })}
								</Text>
							</Stack>
						) : (
							<Inline
								xcss={cx(
									styles.panelHeader,
									styles.panelHeaderContainer,
									styles.panelHeaderDivider,
								)}
							>
								<Heading as="h2" size="xsmall">
									{formatMessage(messages.sources_panel_title, { numSources })}
								</Heading>
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={formatMessage(messages.close_panel)}
									onClick={handleClose}
								/>
							</Inline>
						)}

						<Stack xcss={styles.panelContent}>
							{sources.map((source, index) => (
								<Box
									key={source.url || index}
									xcss={styles.sourceItem}
									testId={`sources-side-panel-item-${index}`}
								>
									<SourcesSidePanelCard
										source={source}
										index={index}
										searchSessionId={searchSessionId}
										onClickHandler={handleSmartCardClick}
									/>
								</Box>
							))}
						</Stack>
					</Box>,
					document.body,
				)}
		</>
	);
};
